import { Injectable, inject } from '@angular/core';
import {EmployeeService, ProblemInstanceService, Visit} from "planner-shared";
import {RouteTaskView} from "../route-task-view.model";
import {RouteTaskService} from "./route-task.service";
import {TimeService} from "@shared";

const UNKNOWN_ADDRESS = "UNKNOWN_ADDRESS";

const RIDE_SHARING_TRANSLATION_MAP = {
    "DRIVER": {
        "PICK_UP": "RIDE_SHARING_PICK_UP",
        "DROP_OFF": "RIDE_SHARING_DROP_OFF"
    },
    "PASSENGER":
        {
            "PICK_UP": "RIDE_SHARING_PICKED_UP",
            "DROP_OFF": "RIDE_SHARING_DROPPED_OFF"
        }
};

@Injectable({
    providedIn: 'root'
})
export class RouteVisualizationService {
    private readonly employeeService = inject(EmployeeService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly routeTaskService = inject(RouteTaskService);
    private readonly timeService = inject(TimeService);


    constructor() {
        this.loadOffice();
    }

    private officeAddress: string;

    public getRideSharingCompanionsName(routeTask: RouteTaskView): string {
        let employeeNames = '';
        const travelPartnersCount = routeTask.rideSharingConfig.travelPartners.length;
        routeTask.rideSharingConfig.travelPartners.forEach((travelPartner, index) => {
            this.employeeService.getEmployee(travelPartner.workHourId).subscribe(employee => {
                employeeNames += employee.employeeName;
            });
            if (travelPartnersCount > 1 && index !== travelPartnersCount - 1) {
                employeeNames += ', ';
            }
        });
        return employeeNames;
    }

    public loadRideSharingTasksDescription(routeTasks: RouteTaskView[], isPdfView: boolean): RouteTaskView[] {
        routeTasks.filter(task => task.rideSharingConfig).forEach(rideSharingTask => {
            rideSharingTask.serviceName = RIDE_SHARING_TRANSLATION_MAP[rideSharingTask.rideSharingConfig.role][rideSharingTask.rideSharingConfig.action];
            if (!isPdfView) {
                rideSharingTask.name = rideSharingTask.address;
            }
        });
        return routeTasks;
    }

    public getTaskFromAddress(taskIndex: number, routeTasks: RouteTaskView[]): string {
        if (taskIndex === 0) {
            return this.officeAddress;
        } else {
            return routeTasks[taskIndex - 1].address ? routeTasks[taskIndex - 1].address : UNKNOWN_ADDRESS;
        }
    }

    public getTaskToAddress(taskIndex, routeTasks: RouteTaskView[]): string {
        return routeTasks[taskIndex].address ? routeTasks[taskIndex].address : UNKNOWN_ADDRESS;
    }

    public compactRideSharingTasks(routeTasks: RouteTaskView[], routeSolution: Visit[]): RouteTaskView[] {
        routeTasks
            .forEach((routeTask, taskIndex) => {
                if (taskIndex > 0 && taskIndex < routeTasks.length - 1) {
                    if (routeTasks[taskIndex - 1].rideSharingConfig && routeTasks[taskIndex + 1].rideSharingConfig) {
                        routeTasks[taskIndex - 1].rideSharingConfig.travelPartners
                            .forEach(partner => {
                                let partnerRouteTasks: RouteTaskView[];
                                this.routeTaskService
                                    .mapToRouteTasks(routeSolution
                                        .filter(task => task.workHourId === partner.workHourId))
                                    .subscribe(mappedTasks => {
                                        partnerRouteTasks = mappedTasks.filter(value => !value.rideSharingConfig);
                                        let isTaskSynced = partnerRouteTasks.find(visit =>
                                            visit.duration === routeTask.duration &&
                                            visit.name === routeTask.name &&
                                            visit.fromTime === routeTask.fromTime &&
                                            visit.toTime === routeTask.toTime
                                        );
                                        if (isTaskSynced) {
                                            routeTasks[taskIndex].travelTimeFromPrev = routeTasks[taskIndex - 1].travelTimeFromPrev;
                                            routeTasks.splice(taskIndex - 1, 1);
                                            routeTasks.splice(taskIndex, 1);
                                        }
                                    });
                            });
                    }
                }
            });
        return routeTasks;
    }

    public calculateTravelTimeToOffice(routeTasks: RouteTaskView[], endLocationArrivalTime: number): string {
        const TIME_ZONE_HOURS_OFFSET = 1;
        const endLocationArrivalEpoch = new Date((endLocationArrivalTime + 3600 * TIME_ZONE_HOURS_OFFSET) * 1000);
        const endLocationArrivalConverted =
            this.timeService.formatToTimeString(endLocationArrivalEpoch.getUTCHours())
            + ':' +
            this.timeService.formatToTimeString(endLocationArrivalEpoch.getUTCMinutes());
        return this.timeService
            .timeDifference(routeTasks[routeTasks.length - 1].toTime, endLocationArrivalConverted)
            .toString();
    }

    private loadOffice(): void {
        this.problemInstanceService.getProblemInstance()
            .subscribe(instanceResult =>
                this.officeAddress = instanceResult.problemInstance.officeInfo.streetAddressNameNumberAndLetter);
    }
}
